/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useState } from 'react';
import { navigate, RouteComponentProps, useLocation } from '@reach/router';
import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';

import {
  EvConsultDiscussionTopic,
  HvConsultDiscussionTopic,
  PreferredDay,
  PreferredTime,
  RequestCategory,
} from '../../../../__generated__/pgeplus-types';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';
import { detailsForm } from '../forms';
import { useDetailsForm } from './useDetailsForm';
import useAccountCustomer from '../../../../hooks/useAccountCustomer';
import useAuth from '../../../../hooks/useAuth';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import ROUTES from '../../../../routes';
import useUtil from '../../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import { ConsultationLocationState } from '../types';

import Backdrop from '../../../backdrop';
import { CheckBoxes } from '../../../pge-plus-common/CheckBoxes';
import CommonButton from '../../../pge-plus-common/CommonButton';
import { DropdownMenu } from '../../../pge-plus-common/DropdownMenu';
import { FullDivider } from '../../../pge-plus-common/FullDivider';
import RadioButtonGroup from '../../../pge-plus-common/RadioButtonGroup';
import { ProceedOrCancelButton } from '../../../pge-plus-common/StepperForm';
import { Question } from '../../../pge-plus-common/Typography';
import { ContactInfoBox } from '../../../pge-plus-form/ContactInfo/ContactInfoBox';
import { OtherContactInfo } from './OtherContactInfo';
import { SectionInfo } from './SectionInfo';
import {
  displayCustomerName,
  displayPhoneNumberFormat,
} from '../../../../util/format';
import { AccountDetail } from '../../../../__generated__/pge-types';
import TextField from '../../../text-field';
import useGetConsultationRequests from '../../../../hooks/pge-plus/useGetConsultationRequests';
import colors from '../../../../themes/main-colors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      margin: '0 auto',
      maxWidth: '800px',
      '& .MuiFormControl-root': {
        background: 'transparent',
      },
    },
    text: {
      marginTop: '20px',
      '& .MuiTypography-body1': {
        fontSize: '18px',
        fontWeight: 'normal',
      },
      '& .MuiButtonBase-root': {
        padding: '0 9px',
      },
      '& .MuiFormControlLabel-root': {
        marginRight: 0,
      },
    },
    gridContainer: {
      columnGap: theme.spacing(4),
      display: 'flex',
      justifyContent: 'center',
      [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
        columnGap: 'unset',
        justifyContent: 'space-between',
      },
    },
    otherContactGrid: {
      width: '100%',
      margin: '0 auto',
      marginTop: theme.spacing(4),
      '& .MuiFormControl-root': {
        background: 'transparent',
      },
      '& .MuiInputBase-input': {
        background: '#FFF',
      },
    },
    topMargin: { marginTop: theme.spacing(4) },
    serviceOptions: {
      marginTop: theme.spacing(4),
      justifyContent: 'space-between',
    },
    heading: {
      fontWeight: 700,
      fontFamily: 'Untitled-Sans',
      margin: theme.spacing(0, 0, 3, 0),
      [`@media (min-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: '20px',
      },
    },
  }),
);

export const Details: React.FC<RouteComponentProps> = (
  _: RouteComponentProps,
) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const state = location?.state as ConsultationLocationState;
  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_CONSULTATION,
  );
  const classes = useStyles();
  const theme = useTheme();
  const { form } = detailsForm(content);
  const { customer, loading } = useAccountCustomer();
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();

  const previousRoute = isAuthenticated
    ? ROUTES.PGE_PLUS_CONSULTATION_CHOOSE_ACCOUNT
    : ROUTES.PGE_PLUS_CONSULTATION_SELECT_ADDRESS;

  useEffect(() => {
    if (!Boolean(state?.address)) {
      void navigate(previousRoute);
    } else {
      void form.setValue('address', state?.address);
      void form.setValue(
        'associationWithAddress',
        state?.associationWithAddress,
      );
    }
  }, []);
  const phoneNumber = customer?.contactDetails?.filter(
    contact => contact?.contactType === 'PNP',
  )?.[0]?.contactValue;

  const {
    productOptions,
    mainContactOptions,
    dayOfTheWeekOptions,
    timeOfTheDayOptions,
    servicesOptions,
    hvacDiscussionOptions,
    evDiscussionOptions,
    languagePreferenceOptions,
    isSubmitDisabled,
    submitHvConsultationDetails,
    submitEvConsultationDetails,
  } = useDetailsForm({ content });

  const { getIsEligibleforconsultation } = useGetConsultationRequests();

  const handleHVButtonClick = (
    existingItem: boolean,
    item: {
      label: string;
      value: HvConsultDiscussionTopic;
      img: React.ReactNode;
    },
  ) => {
    const val = form.values.hvacDiscussion ?? [];

    if (existingItem) {
      const index = val.indexOf(item.value);
      val.splice(index, 1);
      void form.setValue('hvacDiscussion', val);
    } else {
      val?.push(item.value);
      void form.setValue('hvacDiscussion', val);
    }
  };

  const handleEVButtonClick = (
    existingItem: boolean,
    item: {
      label: string;
      value: EvConsultDiscussionTopic;
      img: React.ReactNode;
    },
  ) => {
    const val = form.values.evDiscussion ?? [];

    if (existingItem) {
      const index = val.indexOf(item.value);
      val.splice(index, 1);
      void form.setValue('evDiscussion', val);
    } else {
      val?.push(item.value);
      void form.setValue('evDiscussion', val);
    }
  };

  const getFullName = () =>
    [form.values.firstName, form.values.middleName, form.values.lastName]
      .filter(Boolean)
      .join(' ');

  const handleOnClick = wrapWithLoader(async product => {
    const category =
      product.value === 'ev'
        ? RequestCategory.EvConsultation
        : RequestCategory.HvacConsultation;

    const requestCategory =
      product.value === 'ev'
        ? content.get('EV_CHARGING') || ''
        : content.get('HVAC') || '';
    try {
      const result = await getIsEligibleforconsultation({
        premiseId: state.encryptedPremiseId,
        category: category,
      });

      const isEligible = result?.data?.isEligibleForConsultation?.isEligible;
      if (isEligible === false) {
        void navigate(
          isAuthenticated
            ? ROUTES.PGE_PLUS_SECURE_CONSULTATION_INELIGIBLE
            : ROUTES.PGE_PLUS_CONSULTATION_INELIGIBLE,
          {
            state: {
              requestCategory: requestCategory,
            },
          },
        );

        return;
      }
    } catch (e) {
      setErrorNotification(true);
    }
    void form.setValue('product', product.value);
  });

  const handleSubmit = wrapWithLoader(async () => {
    if (form.values.product === 'hvac') {
      const result = await submitHvConsultationDetails(
        state.accountNumber!,
        state.encryptedPersonId!,
        state.encryptedPremiseId!,
        form.values,
      );

      if (result?.data?.submitHVConsult.isSuccess) {
        void navigate(
          isAuthenticated
            ? ROUTES.PGE_PLUS_SECURE_CONSULTATION_SUCCESS
            : ROUTES.PGE_PLUS_CONSULTATION_SUCCESS,
          {
            state: {
              name:
                isAuthenticated && form.values.isMainContact === true
                  ? displayCustomerName({
                      mainCustomerName: customer?.personName!,
                    } as AccountDetail)
                  : getFullName(),
              email:
                isAuthenticated && form.values.isMainContact === true
                  ? customer?.email
                  : form.values.emailAddress,
              phone:
                isAuthenticated && form.values.isMainContact === true
                  ? phoneNumber
                  : displayPhoneNumberFormat(form.values.phoneNumber),
            },
          },
        );
      } else {
        setErrorNotification(true);
      }
    }

    if (form.values.product === 'ev') {
      const result = await submitEvConsultationDetails(
        state.accountNumber!,
        state.encryptedPersonId!,
        state.encryptedPremiseId!,
        form.values,
      );

      if (result?.data?.submitEVConsult.isSuccess) {
        void navigate(
          isAuthenticated
            ? ROUTES.PGE_PLUS_SECURE_CONSULTATION_SUCCESS
            : ROUTES.PGE_PLUS_CONSULTATION_SUCCESS,
          {
            state: {
              name:
                isAuthenticated && form.values.isMainContact === true
                  ? displayCustomerName({
                      mainCustomerName: customer?.personName!,
                    } as AccountDetail)
                  : getFullName(),
              email:
                isAuthenticated && form.values.isMainContact === true
                  ? customer?.email
                  : form.values.emailAddress,
              phone:
                isAuthenticated && form.values.isMainContact === true
                  ? phoneNumber
                  : displayPhoneNumberFormat(form.values.phoneNumber),
            },
          },
        );
      } else {
        setErrorNotification(true);
      }
    }
  });

  const handleClickBack = () => {
    if (isAuthenticated && state.hasMultipleAccounts) {
      void navigate(ROUTES.PGE_PLUS_CONSULTATION_CHOOSE_ACCOUNT);
    } else if (isAuthenticated && window.history.length > 3) {
      window.history.go(-2);
    } else {
      isAuthenticated
        ? void navigate(ROUTES.ACCOUNT)
        : void navigate(ROUTES.PGE_PLUS_CONSULTATION_SELECT_ADDRESS);
    }
  };

  if (loading) {
    <Backdrop />;
  }

  return (
    <form onSubmit={form.submit(handleSubmit)}>
      <Grid className={classes.grid}>
        <Grid item className={classes.topMargin}>
          <SectionInfo text={content.get('WHAT_CAN_WE_HELP_YOU_WITH')} />
          <Grid spacing={2} container className={classes.gridContainer}>
            {productOptions.map((product, i) => (
              <Grid item md={4} xs={6} key={i}>
                <CommonButton
                  buttonLabel={product.label}
                  handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                    handleOnClick(product)
                  }
                  isActive={form.values.product === product.value}
                  img={product.img}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        {form.values.product !== undefined && (
          <Fragment>
            {isAuthenticated && (
              <Fragment>
                <Grid item className={classes.topMargin}>
                  <SectionInfo text={content.get('PLEASE_CONFIRM_DETAILS')} />
                  <ContactInfoBox
                    content={content}
                    userInfo={{
                      firstName: displayCustomerName({
                        mainCustomerName: customer?.personName,
                      } as AccountDetail),
                      lastName: undefined,
                      address: form.values.address,
                      emailAddress: customer?.email,
                      middleName: undefined,
                      phoneNumber,
                    }}
                    onPhoneNumberUpdate={(__: string) => undefined}
                  />
                </Grid>
                <Grid item className={classes.topMargin}>
                  <SectionInfo text={content.get('IS_MAIN_POINT_OF_CONTACT')} />
                  <Grid
                    css={{
                      width: 'max-content',
                      margin: '0 auto',
                    }}
                  >
                    <RadioButtonGroup
                      options={mainContactOptions}
                      state={form.values.isMainContact}
                      setState={e => form.setValue('isMainContact', e)}
                    />
                  </Grid>
                </Grid>
              </Fragment>
            )}
            {(!isAuthenticated || form.values.isMainContact === false) && (
              <Grid className={classes.otherContactGrid}>
                <OtherContactInfo content={content} form={form} />
              </Grid>
            )}
            <Grid item className={classes.topMargin}>
              <SectionInfo
                text={content.get('CONSULTATION_LANGUAGE_PREFERENCE')}
              />
            </Grid>
            <Grid item className={classes.topMargin}>
              <DropdownMenu
                name={content.get('LANGUAGE') || ''}
                options={languagePreferenceOptions}
                state={form.values.preferredLanguage || ''}
                setState={e => void form.setValue('preferredLanguage', e)}
                errorMsg={(form.errors.preferredLanguage as string) || ''}
              />
            </Grid>
            <Grid item className={classes.topMargin}>
              <SectionInfo text={content.get('SELECT_BEST_DAY')} />
              <Typography
                variant="body2"
                css={{
                  fontSize: theme.spacing(2.25),
                  lineHeight: '20px',
                }}
              >
                {content.get('PGE_PLUS_SPECIALIST_WILL_CONTACT_YOU')}
              </Typography>
              <Grid item className={classes.topMargin}>
                <SectionInfo text={content.get('DAY_OF_THE_WEEK')} />
              </Grid>
              <Grid item className={classes.topMargin}>
                <Typography variant="body2" className={classes.text}>
                  <CheckBoxes
                    options={dayOfTheWeekOptions}
                    state={form.values.preferredDay || []}
                    setState={e => {
                      void form.setValue('preferredDay', e);
                    }}
                    exclusiveValue={PreferredDay.NoPreference}
                  />
                </Typography>
              </Grid>
              <Grid item className={classes.topMargin}>
                <SectionInfo text={content.get('TIME_OF_THE_WEEK')} />
              </Grid>
              <Grid item className={classes.topMargin}>
                <Typography variant="body2" className={classes.text}>
                  <CheckBoxes
                    options={timeOfTheDayOptions}
                    state={form.values.preferredTimeSlot || []}
                    setState={e => {
                      void form.setValue('preferredTimeSlot', e);
                    }}
                    exclusiveValue={PreferredTime.NoPreference}
                  />
                </Typography>
              </Grid>
            </Grid>
            {form.values.product === 'hvac' && (
              <Fragment>
                <Grid item className={classes.topMargin}>
                  <Question
                    detail={
                      <Typography
                        data-testid="mailing-address-title"
                        className={classes.heading}
                        variant="h4"
                      >
                        {content.get('WHAT_ARE_YOU_LOOKING_FOR')}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid container spacing={2} className={classes.serviceOptions}>
                  {servicesOptions.map((service, i) => (
                    <Grid item md={4} xs={6} key={i}>
                      <CommonButton
                        buttonLabel={service.label}
                        handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                          form.setValue('service', service.value)
                        }
                        isActive={form.values.service === service.value}
                        img={service.img}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Fragment>
            )}
            <Grid item className={classes.topMargin}>
              <SectionInfo
                text={content.get('WHAT_ARE_YOU_INTERESTED_IN_DISCUSSING')}
              />
            </Grid>
            <Grid container spacing={2} className={classes.serviceOptions}>
              {form.values.product === 'hvac' &&
                hvacDiscussionOptions.map(discussion => {
                  const existingItem =
                    form.values.hvacDiscussion?.includes(discussion.value) ??
                    false;

                  return (
                    <Grid item md={4} xs={6}>
                      <CommonButton
                        buttonLabel={discussion.label}
                        handleOnClick={() =>
                          handleHVButtonClick(existingItem, discussion)
                        }
                        isActive={existingItem}
                        img={discussion.img}
                      />
                    </Grid>
                  );
                })}
              {form.values.product === 'ev' &&
                evDiscussionOptions.map(discussion => {
                  const existingItem =
                    form.values.evDiscussion?.includes(discussion.value) ??
                    false;

                  return (
                    <Grid
                      item
                      md={4}
                      xs={6}
                      css={{
                        '&:last-child': {
                          marginRight: 'auto',
                        },
                      }}
                    >
                      <CommonButton
                        buttonLabel={discussion.label}
                        handleOnClick={() =>
                          handleEVButtonClick(existingItem, discussion)
                        }
                        isActive={existingItem}
                        img={discussion.img}
                      />
                    </Grid>
                  );
                })}
            </Grid>
            {((form.values.product === 'hvac' &&
              form.values.hvacDiscussion?.includes(
                HvConsultDiscussionTopic.Other,
              )) ||
              (form.values.product === 'ev' &&
                form.values.evDiscussion?.includes(
                  EvConsultDiscussionTopic.Other,
                ))) && (
              <Grid
                container
                spacing={2}
                css={{
                  marginTop: theme.spacing(1),
                  padding: theme.spacing(1),
                  '& textarea': {
                    width: '100%',
                    padding: '8px',
                    boxSizing: 'border-box',
                    fontFamily: 'Untitled-Sans',
                    fontSize: '16px',
                  },
                  '& .otherTopicLabelPropClassName span': {
                    color: colors.roseRed,
                  },
                }}
              >
                <TextField
                  placeholder={content.get(
                    'PLEASE_DESCRIBE_THE_TOPIC_TO_DISCUSS',
                  )}
                  label={richText(content.get('OTHER_TOPIC'))}
                  multiline
                  rows={4}
                  rowsMax={4}
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      top: '-14px',
                    },
                    className: 'otherTopicLabelPropClassName',
                  }}
                  inputProps={{ maxLength: 500 }}
                  css={{
                    width: '100%',
                  }}
                  {...form.props('otherDiscussion')}
                />
                <Typography variant="caption">
                  {form.values.otherDiscussion?.length ?? 0}/500{' '}
                  {content.get('CHARACTERS')}
                </Typography>
              </Grid>
            )}
          </Fragment>
        )}
        <Grid item className={classes.topMargin}>
          <FullDivider />
        </Grid>
        <Grid
          item
          css={{
            marginTop: theme.spacing(4),
            '& button[type=submit]:not(:disabled)': {
              backgroundColor: colors.orange,
            },
            '& button[type=submit]:not(:disabled):hover': {
              boxShadow: 'none',
              backgroundColor: colors.orangeBrown,
            },
          }}
        >
          <ProceedOrCancelButton
            handleClickBack={handleClickBack}
            nextLabel={content.get('SUBMIT')}
            disable={isSubmitDisabled(form)}
          />
        </Grid>
      </Grid>
    </form>
  );
};
